import { useInfiniteQuery, useTRPC } from "@luckymenu/api-client/react";
import type { BillSummary, ListBillsInput } from "@luckymenu/billing-core";
import type { BillStatus } from "@luckymenu/core";
import { keepPreviousData } from "@tanstack/react-query";
import { useMemo, useRef } from "react";
import { useCan } from "./use-navigation";

/** The bills list's filters as the screen holds them. Empty text is no filter. */
export type BillsFilter = {
	/** Part of a bill number. */
	search: string;
	status: BillStatus | "all";
	/** Business dates, YYYY-MM-DD, both included. */
	from: string;
	to: string;
};

export const NO_BILLS_FILTER: BillsFilter = { search: "", status: "all", from: "", to: "" };

const SEARCH = /^[A-Za-z0-9/-]{1,16}$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The filters as `listBills` takes them, or which one is wrong: a search
 * with other characters than a bill number has, or a start after the end.
 */
export function billsListInput(
	filter: BillsFilter,
	pageSize = 50,
): { ok: true; input: Omit<ListBillsInput, "cursor"> } | { ok: false; field: "search" | "to" } {
	const search = filter.search.trim();
	if (search && !SEARCH.test(search)) return { ok: false, field: "search" };
	const from = DATE.test(filter.from) ? filter.from : undefined;
	const to = DATE.test(filter.to) ? filter.to : undefined;
	if (from && to && from > to) return { ok: false, field: "to" };
	return {
		ok: true,
		input: {
			limit: pageSize,
			...(search ? { search } : {}),
			...(filter.status !== "all" ? { status: filter.status } : {}),
			...(from ? { from } : {}),
			...(to ? { to } : {}),
		},
	};
}

/**
 * More > Bills (P4-T20): newest first, 50 at a time (`loadMore`), filtered
 * by part of the number, status and business dates. Needs `bill.create`.
 */
export function useBillsList(filter: BillsFilter, options: { pageSize?: number } = {}) {
	const trpc = useTRPC();
	const allowed = useCan("bill.create");
	const pageSize = options.pageSize ?? 50;
	const checked = billsListInput(filter, pageSize);
	// A filter being typed wrong keeps the last good one, and its list.
	const lastGood = useRef<Omit<ListBillsInput, "cursor">>({ limit: pageSize });
	if (checked.ok) lastGood.current = checked.input;
	const input = lastGood.current;
	const query = useInfiniteQuery({
		...trpc.staff.billing.listBills.infiniteQueryOptions(input, {
			getNextPageParam: (page) => page.nextCursor ?? undefined,
		}),
		enabled: allowed,
		// While filters change, the last list stays on screen.
		placeholderData: keepPreviousData,
	});
	const bills: BillSummary[] = useMemo(
		() => query.data?.pages.flatMap((p) => p.items) ?? [],
		[query.data],
	);
	return {
		status: query.data
			? ("ready" as const)
			: query.isError
				? ("error" as const)
				: ("loading" as const),
		/** The filter field that is wrong, if any; the list keeps the last good answer. */
		invalid: checked.ok ? null : checked.field,
		bills,
		hasMore: query.hasNextPage,
		loadingMore: query.isFetchingNextPage,
		loadMore: () => void query.fetchNextPage(),
		retry: () => void query.refetch(),
	};
}
